/* eslint-disable @typescript-eslint/no-explicit-any */
import { FormikProps } from "formik";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";

import { OnboardingPageContainer } from "../../components";
import * as commonStyles from "../../components/commonStyles/commonStyles.module.css";
import { FormHeader } from "../../components/FormHeader";
import { SaveAndExitButton } from "../../components/SaveAndExitButton/SaveAndExitButton";
import { SplitPane } from "../../components/SplitPane";
import { SubFormNavigationProps } from "../../components/SubForm";
import { PageType } from "../../constants/pageType";
import { compareIdAzureApplicationStepStatus } from "../../helpers/idAzureStatusHelpers";
import { getIdAzureIntroFormUrl } from "../../helpers/navigationHelpers";
import { useStrings } from "../../localization/localizationHelpers";
import { FormLocalizedTitleStringKey, Programs } from "../../models/programsConfig";
import { SubFormsRenderingModel } from "../../models/sharedFormsModels";
import { IdAzureApplicationStep } from "../../models/status";
import { RouteNames } from "../../partnerConfig/routeNames";
import { OnboardingUrls } from "../../partnerConfig/routes";
import { crmActions } from "../../redux/actions/crmApiActions";
import { getIdAzureApplicationStep } from "../../redux/selectors";
import { getOnboardingSignedIn } from "../../redux/selectors/authSelectors";
import { OnboardingPageAction } from "../../telemetry/models";
import { logPageAction } from "../../telemetry/pageAction";
import { ContactAndStudioSubForm } from "./ContactAndStudioSubForm";
import {
  IdAzureIntroFormId,
  IdAzureIntroSubFormsId,
  IdAzureIntroSubFormsIdsOrder,
  IdAzureIntroSubFormsLocalizedStringIds,
} from "./IdAzureIntroPageConstants";

interface RouteParams {
  pageId: string;
}

const IdAzureIntroPage = () => {
  const params = useParams<RouteParams>();
  const [currentSubFormIndex, setCurrentSubFormIndex] = useState(-1);
  const [idAzureIntroTitle, contactInfoTitle] = useStrings(
    IdAzureIntroSubFormsLocalizedStringIds[FormLocalizedTitleStringKey],
    IdAzureIntroSubFormsLocalizedStringIds[IdAzureIntroSubFormsId.ContactAndStudio]
  );
  const isSignedIn = useSelector(getOnboardingSignedIn);
  const currentStep = useSelector(getIdAzureApplicationStep);
  const history = useHistory();
  const dispatch = useDispatch();
  const formRef: React.RefObject<FormikProps<any>> = useRef<FormikProps<any>>(null);

  useEffect(() => {
    const navigateToIdAzureHome = () => history.replace(OnboardingUrls[RouteNames.IdAzureHome]);

    // TODO: For server-side rendering on this page, this redirection happens assuming the CRM API has previously been called and has updated the redux
    // state to the latest. Otherwise, the ApplicationStep will be Unset and it will always redirect to the Dashboard page.
    // Same thing would happen on GameInformationPage.
    const isIdAzureIntroEnabled =
      isSignedIn &&
      currentStep &&
      compareIdAzureApplicationStepStatus(currentStep, IdAzureApplicationStep.Introduction) >= 0 &&
      compareIdAzureApplicationStepStatus(currentStep, IdAzureApplicationStep.ApplicationComplete) <
        0;

    if (!isIdAzureIntroEnabled) {
      navigateToIdAzureHome();
      return;
    }

    const subFormIndex = IdAzureIntroSubFormsIdsOrder.indexOf(
      params.pageId as IdAzureIntroSubFormsId
    );

    if (subFormIndex < 0) {
      navigateToIdAzureHome();
      return;
    }

    setCurrentSubFormIndex(subFormIndex < 0 ? 0 : subFormIndex);
  }, [params.pageId, history, isSignedIn, currentStep]);

  const subFormsList: ReadonlyDictionary<SubFormsRenderingModel> = {
    [IdAzureIntroSubFormsId.ContactAndStudio]: {
      title: contactInfoTitle,
      renderSubForm: (subFormProps: SubFormNavigationProps) => (
        <ContactAndStudioSubForm {...subFormProps} />
      ),
    },
  };

  const onBackNavigation = () => {
    const previousSubFormIndex = currentSubFormIndex - 1;
    if (previousSubFormIndex >= 0) {
      logPageAction(OnboardingPageAction.BackFormButton);
      history.push(getIdAzureIntroFormUrl(IdAzureIntroSubFormsIdsOrder[previousSubFormIndex]));
    }
  };

  const onSaveFormToCrm = (subFormId: string) => {
    logPageAction(OnboardingPageAction.SaveAndNextFormButton);
    dispatch(
      crmActions.saveCrmData.start(Programs.IdAzure, IdAzureIntroFormId, subFormId, undefined)
    );
  };

  const onForwardNavigation: () => void = () => {
    // Navigate to next sub-form or back to dashboard
    const nextSubFormIndex = currentSubFormIndex + 1;
    if (nextSubFormIndex < IdAzureIntroSubFormsIdsOrder.length) {
      history.push(getIdAzureIntroFormUrl(IdAzureIntroSubFormsIdsOrder[nextSubFormIndex]));
    } else {
      history.push(OnboardingUrls[RouteNames.IdAzureHome]);
    }
  };

  const onCancel = () => {
    logPageAction(OnboardingPageAction.CancelFormButton);
    history.push(OnboardingUrls[RouteNames.IdAzureHome]);
  };

  const renderSubForm = (subFormIndex: number) => {
    const subFormId = IdAzureIntroSubFormsIdsOrder[subFormIndex];
    const subFormProps: SubFormNavigationProps = {
      formRef,
      onCancel: subFormIndex === 0 ? onCancel : undefined,
      onBackNavigation: subFormIndex > 0 ? onBackNavigation : undefined,
      onSaveFormToCrm,
      onForwardNavigation,
    };
    return subFormsList[subFormId].renderSubForm(subFormProps);
  };

  const currentSubFormId = IdAzureIntroSubFormsIdsOrder[currentSubFormIndex];
  return (
    <OnboardingPageContainer pageType={PageType.IdAzureApplication}>
      <SplitPane rightPane={<div></div>}>
        <SaveAndExitButton
          formRef={formRef}
          formId={IdAzureIntroFormId}
          subFormId={currentSubFormId}
          onSaveToCrm={() => onSaveFormToCrm(currentSubFormId)}
          program={Programs.IdAzure}
          programDashboardRouteName={RouteNames.IdAzureHome}
        />
        {currentSubFormIndex >= 0 && (
          <div className={commonStyles.formWrapper}>
            <FormHeader
              subFormsMetadata={subFormsList}
              currentSubFormId={IdAzureIntroSubFormsIdsOrder[currentSubFormIndex]}
              subFormsIdsOrder={IdAzureIntroSubFormsIdsOrder}
              mainFormTitle={idAzureIntroTitle}
            />
            {renderSubForm(currentSubFormIndex)}
          </div>
        )}
      </SplitPane>
    </OnboardingPageContainer>
  );
};

export default IdAzureIntroPage;
